// "What you can make": every format as a card, the three headline types large, the rest by group.
// A card with a showcased sample previews it muted on hover (mouse); a click or tap opens it full size with sound
// (Watch). Nothing downloads before that except the poster (lazy). Without a sample the card shows its drawn
// preview (art.tsx).
// The list starts as the static mirror and is replaced by GET /api/formats when that answers (formats.ts).
import { useEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { FormatArt } from "./art";
import { FormatChip, gelOf } from "./brand";
import { STATIC_FORMATS, arrange, clock, fetchFormats, type ShowFormat } from "./formats";
import { IconMic, IconPlay, useReducedMotion } from "./parts";

export function Showcase({ initial = STATIC_FORMATS, live = true }: { initial?: ShowFormat[]; live?: boolean }) {
  const [list, setList] = useState(initial);
  useEffect(() => {
    if (!live) return;
    let on = true;
    void fetchFormats().then((l) => on && l && setList(l));
    return () => {
      on = false;
    };
  }, [live]);
  const { featured, shelves } = arrange(list);

  return (
    <section id="make" className="lp-section lp-make" aria-labelledby="lp-make">
      <p className="lp-eyebrow">What you can make</p>
      <h2 id="lp-make" className="lp-h2">
        Any video. One conversation.
      </h2>

      <ul className="lp-make-featured" aria-label="The headline formats">
        {featured.map((f) => (
          <FormatCard key={f.id} f={f} feature />
        ))}
      </ul>

      <div className="lp-shelves">
        {shelves.map((s) => (
          <section key={s.group} className="lp-shelf" style={{ ["--span2" as string]: Math.min(s.formats.length, 2), ["--span4" as string]: Math.min(s.formats.length, 4), ...(gelOf(s.group) ? { ["--gel" as string]: gelOf(s.group) } : {}) }} aria-labelledby={`lp-shelf-${slug(s.group)}`}>
            <h3 id={`lp-shelf-${slug(s.group)}`} className="lp-shelf-h">
              {s.group}
            </h3>
            <ul className="lp-shelf-list">
              {s.formats.map((f) => (
                <FormatCard key={f.id} f={f} />
              ))}
            </ul>
          </section>
        ))}
        <p className="lp-more">
          <b>And more on the way.</b>
        </p>
      </div>
    </section>
  );
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export function FormatCard({ f, feature = false }: { f: ShowFormat; feature?: boolean }) {
  const reduced = useReducedMotion();
  const [playing, setPlaying] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [ready, setReady] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);
  const [watching, setWatching] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  const media = useRef<HTMLDivElement>(null);
  const s = f.sample && !posterFailed ? f.sample : null;
  const fit = useFit(media, s ? ratio(s.aspect) : null);
  const Name = feature ? "h3" : "h4";
  const length = s && s.durationS > 0 ? s.durationS : f.lengthS;

  useEffect(() => {
    const v = video.current;
    if (!v) return;
    if (playing) v.play().catch(() => setPlaying(false));
    else v.pause();
  }, [playing, mounted]);

  const start = () => {
    if (!s || watching) return;
    setMounted(true);
    setPlaying(true);
  };
  const watch = () => {
    setPlaying(false);
    setWatching(true);
  };

  return (
    <li
      style={gelOf(f.group) ? ({ "--lp-gel": gelOf(f.group) } as CSSProperties) : undefined}
      className={`lp-fc ${feature ? "lp-fc-feature" : "lp-fc-item"} ${s ? "has-sample" : ""} ${playing && ready ? "is-playing" : ""}`}
      onPointerEnter={(e) => e.pointerType === "mouse" && !reduced && start()}
      onPointerLeave={(e) => e.pointerType === "mouse" && setPlaying(false)}
    >
      <div ref={media} className={`lp-fc-media ${fit === "contain" ? "is-boxed" : ""}`}>
        {s ? (
          <>
            {fit === "contain" && <img className="lp-fc-backdrop" src={s.posterUrl} alt="" loading="lazy" decoding="async" />}
            <img className="lp-fc-poster" src={s.posterUrl} alt={`${f.name}: ${s.title || "a sample"}, made in CAO Studio`} loading="lazy" decoding="async" onError={() => setPosterFailed(true)} />
            {mounted && <video ref={video} className="lp-fc-video" src={s.videoUrl} poster={s.posterUrl} muted loop playsInline preload="auto" aria-hidden="true" tabIndex={-1} onPlaying={() => setReady(true)} />}
            <button type="button" className="lp-fc-play" aria-haspopup="dialog" aria-label={`Watch the ${f.name.toLowerCase()} sample with sound${s.title ? `: ${s.title}` : ""}`} onClick={watch}>
              <span className="lp-fc-badge">
                <IconPlay />
                <span>Watch</span>
                {s.durationS > 0 && <span className="lp-fc-badge-time">{clock(s.durationS)}</span>}
              </span>
            </button>
          </>
        ) : (
          <FormatArt id={f.id} name={f.name} />
        )}
      </div>
      {watching && s && <Watch f={f} s={s} onClose={() => setWatching(false)} />}
      <div className="lp-fc-body">
        <div className="lp-fc-meta">
          <FormatChip id={f.id} />
          {length > 0 && (
            <span className="lp-fc-time cao-timecode">
              <span className="lp-sr">{s && s.durationS > 0 ? "Sample length " : "Usual length "}</span>
              {clock(length)}
            </span>
          )}
        </div>
        <div className="lp-fc-title">
          <Name className="lp-fc-name">{f.name}</Name>
          {f.status === "beta" && <span className="lp-fc-beta">Beta</span>}
        </div>
        <p className="lp-fc-tag">{f.tagline}</p>
        {feature && f.example && (
          <p className="lp-fc-say">
            <IconMic />
            <q>{f.example}</q>
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * The sample full size, with sound and controls, in a modal <dialog> (Esc, the close button or a click outside closes
 * it; focus returns to the card's button). Vertical samples are sized by the viewport's height, wide ones by its width.
 */
export function Watch({ f, s, onClose }: { f: ShowFormat; s: NonNullable<ShowFormat["sample"]>; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const r = ratio(s.aspect);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) {
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", "");
    }
    const html = document.documentElement;
    html.classList.add("lp-watching");
    return () => {
      html.classList.remove("lp-watching");
      if (d?.open && typeof d.close === "function") d.close();
    };
  }, []);
  const titleId = `lp-watch-${f.id}`;
  return (
    <dialog
      ref={ref}
      className={`lp-watch ${r < 1 ? "is-tall" : r === 1 ? "is-square" : "is-wide"}`}
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="lp-watch-inner">
        <div className="lp-watch-screen" style={{ aspectRatio: `${r}` }}>
          <video className="lp-watch-video" src={s.videoUrl} poster={s.posterUrl} controls autoPlay playsInline preload="auto" />
        </div>
        <div className="lp-watch-cap">
          <div className="lp-watch-meta">
            <FormatChip id={f.id} />
            {s.durationS > 0 && <span className="lp-fc-time cao-timecode">{clock(s.durationS)}</span>}
          </div>
          <h3 id={titleId} className="lp-watch-title">
            {s.title || f.name}
          </h3>
          <p className="lp-watch-note">
            Made in CAO Studio from one conversation.
          </p>
          <button type="button" className="lp-watch-close" onClick={onClose} aria-label="Close the video">
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>
    </dialog>
  );
}

/**
 * Cover the frame when the sample's shape is close to it; otherwise show all of it (boxed) over its own blurred
 * poster, so a vertical Short or a square slideshow never loses its captions to a crop. Re-checked on resize,
 * since the frame's shape changes between breakpoints.
 */
function useFit(el: RefObject<HTMLDivElement | null>, sample: number | null): "cover" | "contain" {
  const [fit, setFit] = useState<"cover" | "contain">("cover");
  useEffect(() => {
    const node = el.current;
    if (!node || sample === null) return;
    const check = () => {
      const { width, height } = node.getBoundingClientRect();
      if (width && height) setFit(Math.abs(Math.log(sample / (width / height))) > 0.12 ? "contain" : "cover");
    };
    check();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(check);
    ro.observe(node);
    return () => ro.disconnect();
  }, [el, sample]);
  return fit;
}

/** "9:16" -> 0.5625 */
function ratio(a: string): number {
  const [w, h] = a.split(":").map(Number);
  return w && h ? w / h : 16 / 9;
}
